import {
  Boxes,
  Building2,
  Home,
  LayoutDashboard,
  LayoutGrid,
  Package,
  Truck,
  Workflow,
} from 'lucide-react';
import { WAREHOUSE_ROOT, isWarehousePath } from '../../layouts/areaSwitch';
import { UPSELLING_HIDDEN } from '../../features/upselling/upsellingConstants';

/**
 * The admin's sidebar, with or without the pickup points entry. Built once per
 * variant below: `Sidebar` wants module-level constants.
 */
const buildAdminNavigation = ({ hasPickupPoints }) => [
  { type: 'link', to: '/home', icon: Home, label: 'Inicio' },
  { type: 'link', to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  {
    type: 'section',
    key: 'deliveries',
    to: '/deliveries',
    icon: Package,
    label: 'Deliveries',
    children: [
      { to: '/deliveries', label: 'Historial', end: true },
      { to: '/deliveries/attention', label: 'Alerta' },
      // Upselling is hidden for now, and its routes with it: see `UPSELLING_HIDDEN`.
      ...(UPSELLING_HIDDEN ? [] : [{ to: '/incoming-upsells', label: 'Upselling' }]),
    ],
  },
  { type: 'link', to: '/pickups', icon: Truck, label: 'My Pickups' },
  {
    type: 'group',
    key: 'panel',
    icon: LayoutGrid,
    label: 'Panel de Control',
    children: [
      { to: '/sellers', label: 'Mis sellers' },
      ...(UPSELLING_HIDDEN ? [] : [{ to: '/partnerships', label: 'Mis Alianzas' }]),
      { to: '/my-providers-credentials', label: 'Mis proveedores' },
      { to: '/external-integrations', label: 'Mis servicios externos' },
      // "Mis Zonas" is deliberately absent, and so is its route in
      // `adminRoutes` — hiding only the link leaves the address typeable.
      // The screen is built and the staff keeps it; what is not ready is
      // handing it to the customer. See `staffNavigation`.
      { to: '/pickup-requests/create', label: 'Solicitudes de retiro' },
      { to: '/pickup-requests/assign', label: 'Asignar a manifiesto' },
      // Pickup points are an addon: without it the entry is left out, and its
      // address answers "page does not exist" (`PickupPoints`).
      ...(hasPickupPoints ? [{ to: '/pickup-points', label: 'Mis puntos de retiro' }] : []),
      // Mailing is out of every menu for now: no tree ever served `/mailing`,
      // so the item only ever led to the "page does not exist" screen.
    ],
  },
];

/** The admin's sidebar. The shape is documented in `Sidebar`. */
export const adminNavigation = buildAdminNavigation({ hasPickupPoints: false });

/** The same sidebar for a plan with pickup points: one more entry under the panel. */
export const adminNavigationWithPickupPoints = buildAdminNavigation({ hasPickupPoints: true });

export { WAREHOUSE_ROOT };

/**
 * The sidebar once inside the warehouse module: its own menu. The way back to
 * the OMS is not in it — the sidebar offers it under the user, on both sides
 * (`layouts/areaSwitch`).
 *
 * Grouped the way the module is worked — workflows, then inventory and its
 * masters, then clients. Only what already has a screen is listed: a menu with
 * dead destinations teaches people to distrust what they see.
 */
export const adminWarehouseNavigation = [
  {
    type: 'group',
    key: 'warehouse-workflows',
    icon: Workflow,
    label: 'Workflows',
    children: [
      { to: `${WAREHOUSE_ROOT}/orders`, label: 'Órdenes' },
      { to: `${WAREHOUSE_ROOT}/receipts`, label: 'Recepción' },
      { to: `${WAREHOUSE_ROOT}/putaway`, label: 'Almacenado' },
    ],
  },
  {
    type: 'group',
    key: 'warehouse-inventory',
    icon: Boxes,
    label: 'Inventario y ubicaciones',
    children: [
      { to: `${WAREHOUSE_ROOT}/inventory`, label: 'Inventario' },
      { to: `${WAREHOUSE_ROOT}/movements`, label: 'Movimientos' },
      { to: `${WAREHOUSE_ROOT}/products`, label: 'Productos' },
      { to: `${WAREHOUSE_ROOT}/supplies`, label: 'Insumos' },
      { to: `${WAREHOUSE_ROOT}/locations`, label: 'Ubicaciones' },
    ],
  },
  { type: 'link', to: `${WAREHOUSE_ROOT}/clients`, icon: Building2, label: 'Clientes' },
];

/**
 * Which menu the admin's sidebar shows.
 *
 * The module is a place, not a switch: being under `/warehouse` is what turns
 * the sidebar into the warehouse's, so the mode survives a reload, can be
 * linked to, and leaving it is just navigating away. A plain function, like
 * `pickRoutes`, so the decision can be asserted without mounting a layout.
 * The platform's menu is the same with or without the module: the door into
 * the WMS is not a menu entry (`layouts/areaSwitch`).
 *
 * Every answer is a module-level constant, which `Sidebar` requires.
 *
 * @param {object} params
 * @param {boolean} params.hasWarehouse - Whether the plan carries the module.
 * @param {boolean} [params.hasPickupPoints=false] - Whether the plan carries pickup points.
 * @param {string} params.pathname
 * @returns {{navigation: Array<object>, inWarehouse: boolean}}
 */
export const pickAdminNavigation = ({ hasWarehouse, hasPickupPoints = false, pathname }) => {
  const platform = hasPickupPoints ? adminNavigationWithPickupPoints : adminNavigation;

  // Without the module the address answers "page does not exist", and that
  // screen keeps the platform's sidebar around it.
  if (!hasWarehouse) return { navigation: platform, inWarehouse: false };

  const inWarehouse = isWarehousePath(pathname);

  return {
    navigation: inWarehouse ? adminWarehouseNavigation : platform,
    inWarehouse,
  };
};
